import React, { useEffect, useState } from 'react';

const WEEKDAYS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
const MONTHS = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
];

function parseISO(value: string): Date | null {
    if (!value) return null;
    const [year, month, day] = value.split('-').map(Number);
    if (!year || !month || !day) return null;
    return new Date(Date.UTC(year, month - 1, day));
}

function toISO(date: Date): string {
    return [
        date.getUTCFullYear(),
        String(date.getUTCMonth() + 1).padStart(2, '0'),
        String(date.getUTCDate()).padStart(2, '0'),
    ].join('-');
}

function formatField(value: string): string {
    const date = parseISO(value);
    if (!date) return 'Select date';
    return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        timeZone: 'UTC',
    });
}

function sameDay(a: Date, b: Date | null): boolean {
    return !!b && toISO(a) === toISO(b);
}

function addDays(date: Date, days: number): Date {
    const next = new Date(date);
    next.setUTCDate(next.getUTCDate() + days);
    return next;
}

interface Props {
    from: string;
    to: string;
    error: string | null;
    onFromChange: (value: string) => void;
    onToChange: (value: string) => void;
    onClear: () => void;
    onApply: () => void;
    /** Anchor under the trigger; default left (Sync Tracking filter bar). */
    align?: 'left' | 'right';
    /** Shown in the panel header under "DATE RANGE". */
    maxDaysHint?: number;
}

/**
 * Branded, keyboard-accessible calendar used instead of the browser's native
 * date input popup (which cannot be styled and looks different on every OS).
 *
 * Renders as an absolutely positioned panel relative to a `relative` parent
 * that wraps both the trigger button and this component.
 */
export const DateRangeCalendar: React.FC<Props> = ({
    from,
    to,
    error,
    onFromChange,
    onToChange,
    onClear,
    onApply,
    align = 'left',
    maxDaysHint = 30,
}) => {
    const [activeField, setActiveField] = useState<'from' | 'to'>(from && !to ? 'to' : 'from');
    const initial = parseISO(to || from) ?? new Date();
    const [viewYear, setViewYear] = useState(initial.getUTCFullYear());
    const [viewMonth, setViewMonth] = useState(initial.getUTCMonth());

    useEffect(() => {
        const selected = parseISO(activeField === 'from' ? from : to);
        if (!selected) return;
        setViewYear(selected.getUTCFullYear());
        setViewMonth(selected.getUTCMonth());
    }, [activeField]); // Deliberately follows the field, not every date click.

    const fromDate = parseISO(from);
    const toDate = parseISO(to);
    const monthStart = new Date(Date.UTC(viewYear, viewMonth, 1));
    const gridStart = addDays(monthStart, -monthStart.getUTCDay());
    const days = Array.from({ length: 42 }, (_, i) => addDays(gridStart, i));

    const moveMonth = (delta: number) => {
        const next = new Date(Date.UTC(viewYear, viewMonth + delta, 1));
        setViewYear(next.getUTCFullYear());
        setViewMonth(next.getUTCMonth());
    };

    const chooseDate = (date: Date) => {
        const value = toISO(date);
        if (activeField === 'from') {
            onFromChange(value);
            if (toDate && date > toDate) onToChange('');
            setActiveField('to');
            return;
        }
        if (fromDate && date < fromDate) {
            onFromChange(value);
            onToChange('');
            setActiveField('to');
            return;
        }
        onToChange(value);
    };

    const goToday = () => {
        const today = new Date();
        setViewYear(today.getFullYear());
        setViewMonth(today.getMonth());
    };

    return (
        <div
            className={`absolute top-[calc(100%+6px)] z-50 w-[316px] overflow-hidden rounded-[12px] border border-mapper-border-5 bg-white shadow-mapper-dropdown ${
                align === 'right' ? 'right-0' : 'left-0'
            }`}
        >
            <div className="border-b border-mapper-border-3 bg-mapper-surface px-4 pb-3 pt-4">
                <div className="mb-2 text-[11px] font-bold tracking-[0.4px] text-mapper-muted-3">
                    DATE RANGE · UP TO {maxDaysHint} DAYS
                </div>
                <div className="grid grid-cols-2 gap-2">
                    {([
                        ['from', 'FROM', from],
                        ['to', 'TO', to],
                    ] as const).map(([field, label, value]) => (
                        <button
                            key={field}
                            type="button"
                            onClick={() => setActiveField(field)}
                            aria-pressed={activeField === field}
                            className={`min-h-[48px] rounded-[9px] border px-3 py-2 text-left transition-colors ${
                                activeField === field
                                    ? 'border-mapper-teal bg-mapper-teal-bg-2 shadow-[0_0_0_2px_#CDE8E255]'
                                    : 'border-mapper-border-2 bg-white hover:border-mapper-teal-border-3'
                            }`}
                        >
                            <span className="block text-[9.5px] font-bold tracking-[0.5px] text-mapper-muted-3">{label}</span>
                            <span className={`mt-[2px] block text-[12.5px] font-semibold ${value ? 'text-mapper-body-text-3' : 'text-mapper-muted-3'}`}>
                                {formatField(value)}
                            </span>
                        </button>
                    ))}
                </div>
            </div>

            <div className="px-4 pb-3 pt-[14px]">
                <div className="mb-3 flex items-center justify-between">
                    <div className="text-[14px] font-bold text-mapper-text">
                        {MONTHS[viewMonth]} {viewYear}
                    </div>
                    <div className="flex items-center gap-1">
                        <button
                            type="button"
                            onClick={() => moveMonth(-1)}
                            aria-label="Previous month"
                            className="flex h-9 w-9 items-center justify-center rounded-[8px] text-mapper-body-text hover:bg-mapper-gray-bg focus:outline-none focus:ring-2 focus:ring-mapper-teal"
                        >
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6" /></svg>
                        </button>
                        <button
                            type="button"
                            onClick={() => moveMonth(1)}
                            aria-label="Next month"
                            className="flex h-9 w-9 items-center justify-center rounded-[8px] text-mapper-body-text hover:bg-mapper-gray-bg focus:outline-none focus:ring-2 focus:ring-mapper-teal"
                        >
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18l6-6-6-6" /></svg>
                        </button>
                    </div>
                </div>

                <div className="grid grid-cols-7">
                    {WEEKDAYS.map((day, i) => (
                        <div key={`${day}-${i}`} className="flex h-7 items-center justify-center text-[10.5px] font-bold text-mapper-muted-3">
                            {day}
                        </div>
                    ))}
                    {days.map(date => {
                        const isCurrentMonth = date.getUTCMonth() === viewMonth;
                        const isStart = sameDay(date, fromDate);
                        const isEnd = sameDay(date, toDate);
                        const inRange = !!fromDate && !!toDate && date > fromDate && date < toDate;
                        const isToday = toISO(date) === toISO(new Date());
                        return (
                            <button
                                key={toISO(date)}
                                type="button"
                                onClick={() => chooseDate(date)}
                                aria-label={date.toLocaleDateString('en-US', { dateStyle: 'full', timeZone: 'UTC' })}
                                aria-pressed={isStart || isEnd}
                                className={`relative flex h-9 items-center justify-center text-[12.5px] font-semibold transition-colors focus:z-10 focus:outline-none focus:ring-2 focus:ring-mapper-teal ${
                                    isStart || isEnd
                                        ? 'rounded-[8px] bg-mapper-teal text-white shadow-mapper-btn'
                                        : inRange
                                            ? 'bg-mapper-teal-bg-3 text-mapper-teal-hover'
                                            : isCurrentMonth
                                                ? 'rounded-[8px] text-mapper-body-text-3 hover:bg-mapper-gray-bg'
                                                : 'rounded-[8px] text-mapper-muted-5 hover:bg-mapper-gray-bg'
                                }`}
                            >
                                {date.getUTCDate()}
                                {isToday && !isStart && !isEnd && (
                                    <span className="absolute bottom-[3px] h-[3px] w-[3px] rounded-full bg-mapper-teal" />
                                )}
                            </button>
                        );
                    })}
                </div>

                {error && <div role="alert" className="mt-2 text-[11.5px] font-medium text-mapper-red">{error}</div>}
            </div>

            <div className="flex items-center justify-between border-t border-mapper-border-3 bg-mapper-surface px-4 py-3">
                <div className="flex items-center gap-1">
                    <button type="button" onClick={onClear} className="min-h-9 rounded-[8px] px-2 text-[12.5px] font-semibold text-mapper-muted hover:bg-mapper-gray-bg hover:text-mapper-body-text">
                        Clear
                    </button>
                    <button type="button" onClick={goToday} className="min-h-9 rounded-[8px] px-2 text-[12.5px] font-semibold text-mapper-teal-hover hover:bg-mapper-teal-bg">
                        Today
                    </button>
                </div>
                <button
                    type="button"
                    onClick={onApply}
                    disabled={!!error}
                    className={`min-h-9 rounded-[8px] px-4 text-[12.5px] font-semibold ${
                        error
                            ? 'cursor-not-allowed bg-mapper-gray-bg text-mapper-muted-4'
                            : 'bg-mapper-teal text-white shadow-mapper-btn hover:bg-mapper-teal-hover'
                    }`}
                >
                    Apply
                </button>
            </div>
        </div>
    );
};
